import type { PersonaAskerType, PersonaRoleGroup } from "@repo/core/client";
import { cn } from "@ui/lib";
import {
	BriefcaseIcon,
	HandshakeIcon,
	LandmarkIcon,
	type LucideIcon,
	MegaphoneIcon,
	ServerIcon,
	ShoppingCartIcon,
	UserIcon,
	UserRoundIcon,
	UsersIcon,
	WorkflowIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";

/** One icon per role group: the persona avatar never depends on a name. */
export const PERSONA_ROLE_GROUP_ICONS: Record<PersonaRoleGroup, LucideIcon> = {
	finance: LandmarkIcon,
	it: ServerIcon,
	operations: WorkflowIcon,
	executive: BriefcaseIcon,
	end_user: UserIcon,
	procurement: ShoppingCartIcon,
	marketing: MegaphoneIcon,
	sales: HandshakeIcon,
	other: UserRoundIcon,
};

/**
 * An individual's "About them" sentence that mentions other people (a
 * partner, children, someone they care for) gets the two-person icon.
 */
const SHARED_HOUSEHOLD =
	/\b(we|our|with|family|children|child|kids|partner|spouse|parent|parents|husband|wife|dogs?|students|companion|caregiver|care for)\b/i;

/** The icon of an individual persona: one person, or two for a shared household. */
export function personaIndividualIcon(household?: string | null): LucideIcon {
	return household && SHARED_HOUSEHOLD.test(household) ? UsersIcon : UserIcon;
}

/**
 * The persona avatar in a neutral circle: a person icon for an individual
 * (two people when they ask for a household), a role-group icon for someone
 * at an organization. Identity, not a metric, so it wears text tokens only.
 * Decorative by default; pass `labelled` when it stands alone and needs an
 * accessible name (the role group, or "Person", from `personas.card`).
 */
export function PersonaRoleIcon({
	roleGroup,
	askerType,
	household,
	size = "md",
	labelled = false,
	className,
}: {
	/** Null or missing for an individual (and read as "other" for a professional). */
	roleGroup?: PersonaRoleGroup | null;
	/** Missing = professional (every persona created before asker types). */
	askerType?: PersonaAskerType | null;
	/** An individual's "About them" sentence; picks one or two people. */
	household?: string | null;
	size?: "sm" | "md";
	labelled?: boolean;
	className?: string;
}) {
	const t = useTranslations("personas.card");
	const individual = askerType === "individual";
	const group = roleGroup ?? "other";
	const Icon = individual
		? personaIndividualIcon(household)
		: (PERSONA_ROLE_GROUP_ICONS[group] ?? UserRoundIcon);
	return (
		<span
			role={labelled ? "img" : undefined}
			aria-label={
				labelled
					? individual
						? t("individual")
						: t(`roleGroups.${group}`)
					: undefined
			}
			aria-hidden={labelled ? undefined : true}
			className={cn(
				"flex shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground",
				size === "sm" ? "size-7" : "size-9",
				className,
			)}
		>
			<Icon className={size === "sm" ? "size-3.5" : "size-4"} />
		</span>
	);
}
